import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Shield, User as UserIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard",
  revenue_analytics: "Revenue Analytics",
  discount_analytics: "Discount Analytics",
  freebie_analytics: "Freebie Analytics",
  seller_insights: "Seller Insights",
  quotations: "Quotations (Log + Create + Drafts)",
  discount_approvals: "Discount Approvals (Manager)",
  dispatch: "Dispatch Panel",
  products: "Products",
  sellers: "Sellers",
  inventory_allocation: "Inventory Allocation",
  slab_intelligence: "Slab Intelligence",
  mcf_stock_transfers: "MCF Stock Transfers",
  control_panel: "Control Panel (Users + Settings)",
};

const ORDERED_MODULES = [
  "dashboard",
  "revenue_analytics",
  "discount_analytics",
  "freebie_analytics",
  "seller_insights",
  "quotations",
  "discount_approvals",
  "dispatch",
  "products",
  "sellers",
  "inventory_allocation",
  "slab_intelligence",
  "mcf_stock_transfers",
  "control_panel",
];

/**
 * Approve + assign-permissions modal.
 *
 * Props:
 * - open, onClose
 * - user        — the user object being edited
 * - mode        — 'approve' | 'edit'
 * - onSaved     — () => refetch users
 */
export default function UserPermissionsModal({ open, onClose, user, mode = "edit", onSaved }) {
  const [role, setRole] = useState("user");
  const [permissions, setPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setRole(user.role || "user");
    setPermissions(
      // When approving a brand-new pending user, default to ALL modules ticked so the admin
      // can quickly grant full access; they can also untick the ones they don't want.
      mode === "approve" && (user.permissions || []).length === 0
        ? [...ORDERED_MODULES]
        : [...(user.permissions || [])]
    );
  }, [user, mode]);

  if (!user) return null;

  const toggle = (m) => {
    setPermissions((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const selectAll = () => setPermissions([...ORDERED_MODULES]);
  const clearAll = () => setPermissions([]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        role,
        permissions: role === "admin" ? [] : permissions,
      };
      if (mode === "approve") {
        payload.status = "active";
      }
      await axios.put(`${API}/users/${user.user_id}/permissions`, payload, { withCredentials: true });
      toast.success(mode === "approve" ? `${user.name || user.email} approved` : "Permissions updated");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="user-permissions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "approve" ? (
              <>
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                Approve user
              </>
            ) : (
              <>
                <Shield className="h-5 w-5 text-amber-600" />
                Edit permissions
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {user.name || user.email} · {user.email}
          </DialogDescription>
        </DialogHeader>

        {/* Role */}
        <div className="space-y-2">
          <Label className="text-[12px]">Role</Label>
          <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-2">
            <label className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${role === "user" ? "border-accent bg-accent/5" : "border-slate-200"}`}>
              <RadioGroupItem value="user" data-testid="permissions-role-user" />
              <UserIcon className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-[13px]">User</span>
            </label>
            <label className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${role === "admin" ? "border-amber-400 bg-amber-50/40" : "border-slate-200"}`}>
              <RadioGroupItem value="admin" data-testid="permissions-role-admin" />
              <Shield className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-[13px]">Admin</span>
            </label>
          </RadioGroup>
          {role === "admin" && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
              Admins automatically get access to all modules. Module checkboxes below are ignored.
            </p>
          )}
        </div>

        {/* Module permissions */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[12px]">Module Access</Label>
            <div className="flex items-center gap-2 text-[11px]">
              <button type="button" onClick={selectAll} className="text-accent hover:underline" disabled={role === "admin"} data-testid="permissions-select-all">Select all</button>
              <span className="text-slate-300">·</span>
              <button type="button" onClick={clearAll} className="text-slate-500 hover:text-red-600" disabled={role === "admin"} data-testid="permissions-clear-all">Clear</button>
            </div>
          </div>
          <div className={`rounded-lg border border-slate-200 divide-y divide-slate-100 ${role === "admin" ? "opacity-50 pointer-events-none" : ""}`}>
            {ORDERED_MODULES.map((m) => (
              <label key={m} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                <Checkbox
                  checked={permissions.includes(m)}
                  onCheckedChange={() => toggle(m)}
                  data-testid={`permissions-checkbox-${m}`}
                />
                <span className="text-[13px] text-slate-800">{MODULE_LABELS[m] || m}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">{permissions.length} of {ORDERED_MODULES.length} modules selected</p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="permissions-modal-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#32B4AC] hover:bg-[#2A9A93] text-white" data-testid="permissions-modal-save">
            {saving ? "Saving…" : (mode === "approve" ? "Approve & Save" : "Save Permissions")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
